import { Component, h, Host, Prop, State, Element } from '@stencil/core';
import {
  formatTableBody,
  getColWidth,
  handleTableScroll,
  onResize,
  PrivateStateVars,
  renderTableBody,
  renderTableHeader,
  renderTableColumnGroup,
  renderTableCaption,
  renderTableScrollBar,
  setColumnTypes,
  tableTopContent,
  applyAriaSort,
  handleKeyDown,
  handleMouseDown,
  handleFocus,
  handleBlur,
  getTableA11yArray,
  hasMultipleRowsWithColspanGreaterOneAndIsRowHeader
} from './table-utils';
import { getDataFromPropString } from '@utils/utils';

export type iconData = {
  iconName: string;
  iconDescription: string;
};

export type columnData = {
  columnHeaderText: string;
  columnType: string;
  enableSort: string;
  columnWidthPercentage: string;
  isRowHeader: boolean;
  isCenterAligned: boolean;
};

@Component({
  tag: 'vs-table',
  styleUrl: './_vs-table.scss',
  shadow: false,
})
export class SortableTable {
  @Element() el: HTMLVsTableElement;
  /**
   @shortDescription Table ID */
  @Prop() tableId: string = '';

  /**
   @shortDescription Table Title */
  @Prop() tableTitle: string = 'Table Title';

  /**
   @shortDescription Small Summary for Table */
  @Prop() tableSummary: string = 'Summary for Table';

  /**
   @shortDescription Legend Header for Icons in Table */
  @Prop() iconKeyText?: string = '';

  /**
   @shortDescription Icon Data */
  @Prop() iconLegend?: string = '';

  /**
   @shortDescription Screen reader text for blank cells */
  @Prop() blankCellScreenReader?: string = '';

  /**
   @shortDescription Column which is sorted on first load */
  @Prop() defaultSortColumn: string = '';

  /**
   @shortDescription Order of sort for default sort column at first load */
  @Prop() defaultSortOrder: string = 'descending';

  /**
   @shortDescription Text for Export Button */
  @Prop() exportText: string = 'Export data';

  /**
   @shortDescription Aria LAbel for Export Button */
  @Prop() exportTextAriaLabel: string = 'Export data (Open in new window)';

  /**
   * @shortDescription Flag to enable/disable sorting */
  @Prop() hasCellMerges: boolean;

  /**
   @shortDescription Flag to show/hide export option */
  @Prop() exportTable: boolean;

  /**
   @shortDescription Column Info */
  @Prop() columnInfo: string = '';

  /**
   @shortDescription locale  */
  @Prop() locale?: string = 'en';

  /**
   @shortDescription Cell Data */
  @Prop() rowData: string = '';

  @State() sortBy: string | null = null;
  @State() sortDirection: 'asc' | 'desc' = 'asc';
  @State() screenWidth = 0;
  @State() scrollbarWidth = 0;
  @State() scrollbarThumbWidth = 0;
  @State() previousSortIndex: number = -1;
  @State() stateObject: PrivateStateVars = {
    columnDataList: [],
    iconLegendList: [],
    scrollbarWidth: 0,
    scrollbarThumbWidth: 0,
    docDirFactor: 1,
    originalRowData: [],
    columnTypeList: [],
    colWidthList: [],
    scrollValue: 0,
    tableSize: [0, 0],
  };
  @State() tableRef: HTMLElement;
  @State() scrollBarRef: HTMLInputElement;
  @State() rowDataList: any[] = [];
  @State() sortColumn: any = '';
  @State() parsedRowData: any[] = [];
  @State() previousFocusedCellPos: [number, number] = [0, 0];
  @State() usingKeyboard: boolean = false;
  @State() tablea11yArray: any[] = [];
  @State() tableWidth = 0;
  @State() isMultiLevelTable = false;

  componentWillLoad() {
    this.screenWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    this.stateObject.docDirFactor = document.dir === 'rtl' ? -1 : 1;
    this.parsedRowData = getDataFromPropString(this.rowData);
    this.isMultiLevelTable = hasMultipleRowsWithColspanGreaterOneAndIsRowHeader(this.parsedRowData)
    this.stateObject.columnDataList = getDataFromPropString(this.columnInfo);
    this.stateObject.iconLegendList = getDataFromPropString(this.iconLegend);
    this.stateObject.scrollbarWidth = Math.abs(this.screenWidth - 1140);
    this.scrollbarThumbWidth = this.screenWidth / this.stateObject.columnDataList.length;
    this.stateObject.tableSize = [this.parsedRowData.length, this.stateObject.columnDataList.length];
    this.sortDirection = this.defaultSortOrder === 'descending' ? 'desc' : 'asc';
    setColumnTypes(this);
    getColWidth(this);
    window.addEventListener('resize', () => onResize(this));
    // START GENAI@CHATGPT4
    const parsedDefaultSortColumn = parseInt(this.defaultSortColumn);
    const defaultSortColumn = this.defaultSortColumn && parsedDefaultSortColumn < this.stateObject.tableSize[1] ? parsedDefaultSortColumn : 0;
    // END GENAI@CHATGPT4
    this.sortColumn = defaultSortColumn > 0 ? defaultSortColumn - 1 : undefined;
    this.previousSortIndex = defaultSortColumn > 0 ? defaultSortColumn - 1 : -1;
    this.rowDataList = formatTableBody(this) || [];
    // START GENAI@CHATGPT4
    document.addEventListener('container_expanded', () => {
      onResize(this);
    });
    // END GENAI@CHATGPT4
  }

  componentDidLoad() {
    if (this.parsedRowData.length > 0) {
      // START GENAI@GHCOPILOT
      this.tableWidth = document.getElementById(this.tableId)?.offsetWidth;
      const scrollbarWrapperWidth = document.getElementById('vs-scroll' + this.tableId)?.offsetWidth;
      // END GENAI@GHCOPILOT
      this.stateObject.scrollbarWidth = Math.abs(this.tableWidth - scrollbarWrapperWidth) + 30;
      this.rowDataList = formatTableBody(this) || [];
      this.el.style.setProperty('--scrollbar-thumb-width', `${this.scrollbarThumbWidth}px`);
      if (this.previousSortIndex > -1 && this.hasCellMerges == false) {
        applyAriaSort(this, this.sortColumn, this.sortDirection);
      }
      this.tablea11yArray = getTableA11yArray(this);
      this.tablea11yArray[0][0].setAttribute('tabindex', '0');
      document.addEventListener('keydown', () => {
        this.usingKeyboard = true;
      });
    }
  }
  // START GENAI@CHATGPT4
  render() {
    return (
      this.parsedRowData.length > 0 && (
        <Host>
          {tableTopContent(this)}
          <div
            class="vs-container-xl vs-table-wrapper vs-px-2 vs-px-xl-0 vs-mb-2"
            ref={el => (this.tableRef = el as HTMLElement)}
            onScroll={() => handleTableScroll(this)}
            onKeyDown={event => {
              handleKeyDown(this, event);
            }}
            onMouseDown={() => handleMouseDown(this)}
            onFocusin={event => handleFocus(this, event)}
            onFocusout={event => handleBlur(event)}
          >
            <table id={this.tableId}>
              {renderTableCaption(this)}
              {this.hasCellMerges && renderTableColumnGroup(this)}
              {!this.hasCellMerges && renderTableHeader(this)}
              {renderTableBody(this)}
            </table>
          </div>
          {this.screenWidth < 1200 && renderTableScrollBar(this)}
        </Host>
      )
    );
  }
  // END GENAI@CHATGPT4
}
